import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { Refresh, Add, Delete } from '@material-ui/icons';
import MaterialTable from 'material-table';
import { MuiTableRows } from '../../../themes';
import { BackButton, hasPermission, localization, getDimensionsOfPagination, hasAtLeastOnePermission } from '../../sc-utilities';
import Toolbar from '../../sc-utilities/Toolbar'
import * as actions from './store/tableViewActions';
import * as action from '../../np-core/Auth/store/authActions';
import TableViewDelete from './TableViewDelete';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const TableViewList = props => {
    const { classes, translations, deleting, onFetchTableView, onTableViewCount, fetching, saveColumnsFilter, activeCustomerID, tableColumns, onBulkDeleteTableViews, permissions, redirectHandler, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, TableViewIDs: [] });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: true,
        selection: hasAtLeastOnePermission(permissions, ['table_view_delete']),
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        query['ActiveCustomerID'] = activeCustomerID;
        query['CustomerID'] = null;
        query['q'] = q;
        let count = await onTableViewCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchTableView(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations["table_view"] || 'Table View'}</Typography>
                {hasPermission(permissions, 'table_view_create') ? <Fab
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    className={classes.fab}
                    style={{ borderRadius: '1px' }}
                    onClick={() => redirectHandler(`/admin/tableView/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, TableViewIDs: [] });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteTableViews({ TableViewIDs: deleteModalState.TableViewIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, TableViewIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = TableViewIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, TableViewIDs: TableViewIDs });
    };

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`TableView`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["table_view"] || 'Table View'}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(tableView => tableView.TableViewID))
        },
    ];

    return (
        <Paper>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'table_view_delete') ? (
                <TableViewDelete
                    entityName={translations["table_view"] || 'Table View'}
                    translations={translations}
                    deleting={deleting}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        fetching: state?.admin?.tableView?.fetching,
        deleting: state?.admin?.tableView?.deleting
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onTableViewCount: filter => dispatch(actions.fetchTableViewCount(filter)),
        onFetchTableView: filter => dispatch(actions.fetchTableView(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onBulkDeleteTableViews: data => dispatch(actions.bulkDeleteTableViews(data))
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(TableViewList));


